import { useContentSubpageInstance } from "@plugins/content-subpage/composables/content-subpage-instance.ts";
import { isEmpty, isNull } from "@utils/var.check.ts";
import { useStore } from "vuex";
import { LocationQueryRaw, useRouter } from "vue-router";
import SearchModuleDetailContentSubpage from "@modules/search/components/SearchModuleDetailContentSubpage.vue";
import LessonReviewContentSubpage from "@modules/lesson-review/components/LessonTopicContentSubpage.vue";
import ResourceCopyContentSubpage from "@modules/my-drive/components/ResourceCopyContentSubpage.vue";
import { RESOURCE_TYPE } from "@/types/lesson.ts";
import { COPY_ACTION_TYPE, NAMESPACE } from "@modules/search/service/search.constant.ts";
import { computed } from "vue";
import { EditorRouteParams } from "@modules/editor/types.ts";
import { useRouting } from "@plugins/common/composables/routing.ts";
import ResourceSearchCardUtils from "@utils/resource.search.card.utils.ts";
import { ModuleCardProps } from "@/common/card-types.ts";
import {
  MODULE_COMPLETION_STATUS,
  ModuleCompletionStatus,
} from "@/common/section-display-types.ts";
import { RESOURCE_NODE_TYPE } from "@modules/attempt/service/attempt.constant.ts";
import { LessonResultItem } from "@modules/resource-search/resource-search-types.ts";
import { getLocationLabel } from "@/modules/resource-search/resource-search-helper";
import { formattedViewCount } from "@modules/resource-browse/resource-browse-helper.ts";
import { useI18n } from "vue-i18n";
import DateTimeFormatter from "@utils/datetime.formatter.ts";
import { RESOURCE_DOMAIN } from "@plugins/common/constant/resource.constant.ts";
import {
  deriveCompletionStatus,
  mapToLastAccessedParams,
  mapToSectionDisplayModel,
  RawLastAccessedSource,
  RawPendingActionForCompletion,
  RawSectionInfo,
  resolveLastAccessedDisplay,
} from "@modules/pending-action/service/section-display.utils.ts";

export const useModuleCard = () => {
  const contentSubpageInstance = useContentSubpageInstance();
  const store = useStore();
  const router = useRouter();
  const { urlReady, handleRouteIntervalCheck, target } = useRouting();
  const { t } = useI18n();

  const isStudent = computed<boolean>(() => store.getters["auth/isStudent"]);
  const isTeacher = computed<boolean>(() => store.getters["auth/isTeacher"]);
  const isAssociatedAccount = computed(
    () => store.getters[`${NAMESPACE.AUTH}/isAssociatedAccount`],
  );

  const isPausedDisabledForStudent = (paused?: boolean | null) => isStudent.value && !!paused;

  const buildStudentAttemptFields = (resourceData) => {
    const rawPendingAction = resourceData?.pendingAction;
    const hasAttempted = !isEmpty(rawPendingAction?.lastAttemptedDate);
    if (!isStudent.value || !hasAttempted) {
      // Module the student has not started yet — show only "Not yet accessed".
      return {
        sectionDisplay: undefined,
        lastAccessedDisplay: undefined,
        completionStatus: undefined,
      };
    }

    const pendingActionForStatus: RawPendingActionForCompletion = {
      paused: resourceData?.paused ?? false,
      progressStatus: rawPendingAction?.status,
    };
    const lastAccessedSource: RawLastAccessedSource = {
      lastAccessedDate: rawPendingAction?.lastAttemptedDate,
      lessonExists: true,
    };
    return {
      sectionDisplay: mapToSectionDisplayModel(
        resourceData?.sectionInfo as RawSectionInfo,
        pendingActionForStatus,
      ),
      lastAccessedDisplay: resolveLastAccessedDisplay(
        mapToLastAccessedParams(lastAccessedSource, (raw) =>
          DateTimeFormatter.formatLocalDateTimeToDateOnly(raw),
        ),
        t,
      ),
      completionStatus: deriveCompletionStatus(
        pendingActionForStatus,
        resourceData?.sectionInfo?.sectionDisplayModel,
      ),
    };
  };

  const buildTooltipConfig = (resourceData) => ({
    viewCount: t("search_module_card_views_aria_label", {
      viewCount: formattedViewCount(resourceData?.viewCount),
    }),
    teacherReviewCount: t("search_result_card_view_teacher_review_count_label", {
      count: resourceData?.teacherReviewCount,
    }),
    accessCode: t("module_card_access_code_tooltip"),
    paused: t("module_card_paused_tooltip"),
  });

  const populateModuleCardPropsAddFromLibrary = (resourceData): ModuleCardProps => {
    // populate module card from response params
    return {
      resourceDomain: resourceData?.resourceDomain,
      paused: resourceData?.paused ?? false,
      gameEnabled: resourceData?.gameEnabled,
      title: resourceData?.title,
      thumbnailUrl: resourceData?.thumbnailUrl ?? null,
      viewCount: resourceData?.viewCount,
      teacherReviewCount: resourceData?.teacherReviewCount,
      passcodeEnabled: resourceData?.passcodeEnabled ?? null,
      labelConfig: {
        kind: "domain",
      },
      showResourceCount:
        resourceData?.viewCount !== null && resourceData?.resourceDomain !== "PERSONAL",
      showCopyTo: false,
      isResourceDuplicatable: isStudent.value ? resourceData.duplicatable : true,
      showTeacherReviewCount:
        isTeacher.value &&
        resourceData.resourceDomain &&
        (ResourceSearchCardUtils.checkIfMoe(resourceData?.resourceDomain) ||
          ResourceSearchCardUtils.checkIfCommunity(resourceData?.resourceDomain)),
      showMenuOptions: false,
      showTeacherReview: false,
      ariaLabelConfig: {
        cardComponent: t("search_module_card_title_aria_label", {
          moduleTitle: resourceData?.title,
        }),
        gamification: t("search_module_card_gamification_aria_label"),
        viewCount: t("search_module_card_views_aria_label", {
          viewCount: formattedViewCount(resourceData?.viewCount),
        }),
        viewDetails: t("search_view_details_aria_label", {
          title: resourceData?.title,
        }),
        overflowMenu: t("search_module_card_kebab_aria_label"),
      },
      tooltipConfig: buildTooltipConfig(resourceData),
      ...buildStudentAttemptFields(resourceData),
    };
  };

  const populateModuleCardProps = (resourceData): ModuleCardProps => {
    // populate module card from response params
    return {
      resourceDomain: resourceData?.resourceDomain,
      paused: resourceData?.paused ?? false,
      disabled: isPausedDisabledForStudent(resourceData?.paused),
      gameEnabled: resourceData?.gameEnabled,
      title: resourceData?.title,
      thumbnailUrl: resourceData?.thumbnailUrl ?? null,
      viewCount: resourceData?.viewCount,
      teacherReviewCount: resourceData?.teacherReviewCount,
      passcodeEnabled: resourceData?.passcodeEnabled ?? null,
      labelConfig: {
        kind: "domain",
      },
      showResourceCount:
        resourceData?.viewCount !== null && resourceData?.resourceDomain !== "PERSONAL",
      showCopyTo: !isStudent.value,
      isResourceDuplicatable: isStudent.value ? resourceData.duplicatable : true,
      showTeacherReviewCount:
        isTeacher.value &&
        resourceData.resourceDomain &&
        (ResourceSearchCardUtils.checkIfMoe(resourceData?.resourceDomain) ||
          ResourceSearchCardUtils.checkIfCommunity(resourceData?.resourceDomain)),
      showMenuOptions: !isStudent.value,
      showTeacherReview:
        isTeacher.value &&
        resourceData.resourceDomain &&
        (ResourceSearchCardUtils.checkIfMoe(resourceData?.resourceDomain) ||
          ResourceSearchCardUtils.checkIfCommunity(resourceData?.resourceDomain)),
      ariaLabelConfig: {
        cardComponent: t("search_module_card_title_aria_label", {
          moduleTitle: resourceData?.title,
        }),
        gamification: t("search_module_card_gamification_aria_label"),
        viewCount: t("search_module_card_views_aria_label", {
          viewCount: formattedViewCount(resourceData?.viewCount),
        }),
        viewDetails: t("search_view_details_aria_label", {
          title: resourceData?.title,
        }),
        overflowMenu: t("search_module_card_kebab_aria_label"),
      },
      tooltipConfig: buildTooltipConfig(resourceData),
      ...buildStudentAttemptFields(resourceData),
    };
  };

  const populateModuleCardPropsStarredResource = (resourceData): ModuleCardProps => {
    return {
      resourceDomain: resourceData?.resourceDomain,
      paused: resourceData?.paused ?? false,
      gameEnabled: resourceData?.gameEnabled,
      title: resourceData?.title,
      thumbnailUrl: resourceData?.thumbnailUrl ?? null,
      passcodeEnabled: resourceData?.passcodeEnabled ?? null,
      labelConfig: {
        kind: "domain",
      },
      showResourceCount: false,
      showTeacherReviewCount: false,
      showCopyTo: false,
      showMenuOptions: false,
      showTeacherReview: false,
      isResourceDuplicatable: isStudent.value ? (resourceData?.duplicatable ?? true) : true,
      ariaLabelConfig: {
        cardComponent: t("search_module_card_title_aria_label", {
          moduleTitle: resourceData?.title,
        }),
        gamification: t("search_module_card_gamification_aria_label"),
        viewDetails: t("search_view_details_aria_label", {
          title: resourceData?.title,
        }),
      },
      tooltipConfig: buildTooltipConfig(resourceData),
    };
  };

  /**
   * Paused is a resource-level state — same label for both roles, independent of a
   * student's own attempt data (mirrors `isPausedDisabledForStudent`'s grey-out, which
   * also doesn't gate on attempt data). Otherwise: Student sees own progress
   * (Complete/Incomplete, same as MOE Library); Teacher has no personal completion.
   */
  const resolveClassGroupCompletionStatus = (
    paused: boolean,
    hasStudentAttemptData: boolean,
    pendingActionForStatus: RawPendingActionForCompletion,
    sectionDisplayModel?: RawSectionInfo["sectionDisplayModel"],
  ): ModuleCompletionStatus | undefined => {
    if (paused) {
      return MODULE_COMPLETION_STATUS.PAUSED;
    }
    if (!isStudent.value) {
      return undefined;
    }
    return hasStudentAttemptData
      ? deriveCompletionStatus(pendingActionForStatus, sectionDisplayModel)
      : undefined;
  };

  const populateModuleCardPropsClassGroupResource = (resourceData): ModuleCardProps => {
    const paused = resourceData?.paused ?? false;
    const hasStudentAttemptData =
      !isEmpty(resourceData?.pendingAction) && !isEmpty(resourceData?.sectionInfo);
    const pendingActionForStatus: RawPendingActionForCompletion = {
      paused,
      progressStatus: resourceData?.pendingAction?.status,
    };
    const studentAttemptFields = hasStudentAttemptData
      ? {
          sectionDisplay: mapToSectionDisplayModel(
            resourceData?.sectionInfo as RawSectionInfo,
            pendingActionForStatus,
          ),
          lastAccessedDisplay: resolveLastAccessedDisplay(
            mapToLastAccessedParams(
              {
                lastAccessedDate: resourceData?.pendingAction?.lastAccessedDate,
                lastAttemptedDate: resourceData?.pendingAction?.lastAttemptedDate,
                lessonExists: true,
              } as RawLastAccessedSource,
              (raw) => DateTimeFormatter.formatLocalDateTimeToDateOnly(raw),
            ),
            t,
          ),
        }
      : {};
    const completionStatus = resolveClassGroupCompletionStatus(
      paused,
      hasStudentAttemptData,
      pendingActionForStatus,
      resourceData?.sectionInfo?.sectionDisplayModel,
    );
    return {
      resourceDomain: resourceData?.resourceDomain,
      groupLabel: resourceData?.groupName ?? null,
      paused,
      disabled: isPausedDisabledForStudent(paused),
      gameEnabled: resourceData?.gameEnabled,
      title: resourceData?.title,
      thumbnailUrl: resourceData?.thumbnailUrl ?? null,
      passcodeEnabled: resourceData?.passcodeEnabled ?? null,
      labelConfig: {
        kind: "domain",
      },
      showResourceCount: false,
      showTeacherReviewCount: false,
      showCopyTo: false,
      showMenuOptions: false,
      showTeacherReview: false,
      isResourceDuplicatable: isStudent.value ? (resourceData?.duplicatable ?? true) : true,
      ...studentAttemptFields,
      completionStatus,
      ariaLabelConfig: {
        cardComponent: t("search_module_card_title_aria_label", {
          moduleTitle: resourceData?.title,
        }),
        gamification: t("search_module_card_gamification_aria_label"),
        viewDetails: t("search_view_details_aria_label", {
          title: resourceData?.title,
        }),
      },
      tooltipConfig: buildTooltipConfig(resourceData),
    };
  };

  const populateModuleCardPropsCalendarAttachment = (
    resourceData,
    isFullModuleAttached = false,
  ): ModuleCardProps => {
    const rawSectionDisplay = mapToSectionDisplayModel(
      resourceData?.sectionInfo as RawSectionInfo,
      { paused: false },
      isFullModuleAttached,
    );
    const isClassGroupResource = resourceData?.studentGroupResourceType === "CLASS_GROUP_RESOURCE";

    return {
      resourceDomain: resourceData?.resourceDomain,
      groupLabel: isClassGroupResource ? (resourceData?.groupName ?? null) : null,
      paused: resourceData?.paused ?? false,
      disabled: isPausedDisabledForStudent(resourceData?.paused),
      gameEnabled: resourceData?.gameEnabled,
      title: resourceData?.title,
      thumbnailUrl: resourceData?.thumbnailUrl ?? null,
      passcodeEnabled: resourceData?.passcodeEnabled ?? null,
      labelConfig: {
        kind: "domain",
      },
      showResourceCount: false,
      showTeacherReviewCount: false,
      showCopyTo: false,
      showMenuOptions: false,
      showTeacherReview: false,
      isResourceDuplicatable: true,
      sectionDisplay: rawSectionDisplay.hasSections ? rawSectionDisplay : undefined,
      ariaLabelConfig: {
        cardComponent: t("search_module_card_title_aria_label", {
          moduleTitle: resourceData?.title,
        }),
        gamification: t("search_module_card_gamification_aria_label"),
        viewDetails: t("search_view_details_aria_label", {
          title: resourceData?.title,
        }),
      },
      tooltipConfig: buildTooltipConfig(resourceData),
    };
  };

  const mapPendingActionTypeToResourceDomain = (pendingActionType) => {
    switch (pendingActionType) {
      case "MOE":
        return RESOURCE_DOMAIN.MOE;
      case "CLASS_GROUP":
        return RESOURCE_DOMAIN.ASSIGNMENT;
      case "PERSONAL":
      default:
        return RESOURCE_DOMAIN.PERSONAL;
    }
  };

  const populateModuleCardPropsSelfStudy = (pendingAction, sectionInfo): ModuleCardProps => {
    const isClassGroup = pendingAction?.pendingActionType === "CLASS_GROUP";
    const pendingActionForStatus: RawPendingActionForCompletion = {
      paused: pendingAction?.paused ?? false,
      progressStatus: pendingAction?.progressStatus,
    };
    return {
      resourceDomain: mapPendingActionTypeToResourceDomain(pendingAction?.pendingActionType),
      groupLabel: isClassGroup
        ? (pendingAction?.studentGroup?.name ?? pendingAction?.groupName ?? null)
        : null,
      paused: pendingAction?.paused ?? false,
      disabled: isPausedDisabledForStudent(pendingAction?.paused),
      gameEnabled: pendingAction?.gameEnabled,
      title: pendingAction?.title,
      thumbnailUrl: pendingAction?.thumbnailUrl ?? null,
      passcodeEnabled: pendingAction?.passcodeEnabled ?? null,
      labelConfig: {
        kind: "domain",
      },
      showResourceCount: false,
      showTeacherReviewCount: false,
      showCopyTo: false,
      showMenuOptions: false,
      showTeacherReview: false,
      isResourceDuplicatable: true,
      sectionDisplay: mapToSectionDisplayModel(
        sectionInfo as RawSectionInfo,
        pendingActionForStatus,
      ),
      lastAccessedDisplay: resolveLastAccessedDisplay(
        mapToLastAccessedParams(pendingAction as RawLastAccessedSource, (raw) =>
          DateTimeFormatter.formatLocalDateTimeToDateOnly(raw),
        ),
        t,
      ),
      completionStatus: deriveCompletionStatus(
        pendingActionForStatus,
        sectionInfo?.sectionDisplayModel,
      ),
      ariaLabelConfig: {
        cardComponent: t("search_module_card_title_aria_label", {
          moduleTitle: pendingAction?.title,
        }),
        gamification: t("search_module_card_gamification_aria_label"),
        viewDetails: t("search_view_details_aria_label", {
          title: pendingAction?.title,
        }),
      },
      tooltipConfig: buildTooltipConfig(pendingAction),
    };
  };

  /**
   * Planner attach / after-attached display (SLS-12277 Bước 8) — always hides view/review
   * count, even for MOE Library/Community Gallery resources (technical-design.md §2.6).
   *
   * `isFullModuleAttached` isolates the still-evolving Planner-only section-exclusion
   * rule (AC#106/#107, impact-assessment §7) behind a single, clearly-named flag so it
   * can be adjusted independently of the canonical #105/#203 logic.
   */
  const populateModuleCardPropsPlannerAttachment = (
    resourceData,
    isFullModuleAttached = false,
  ): ModuleCardProps => {
    return {
      resourceDomain: resourceData?.resourceDomain,
      paused: resourceData?.paused ?? false,
      gameEnabled: resourceData?.gameEnabled,
      title: resourceData?.title,
      thumbnailUrl: resourceData?.thumbnailUrl ?? null,
      passcodeEnabled: resourceData?.passcodeEnabled ?? null,
      labelConfig: {
        kind: "domain",
      },
      showResourceCount: false,
      showTeacherReviewCount: false,
      showCopyTo: false,
      showMenuOptions: false,
      showTeacherReview: false,
      isResourceDuplicatable: true,
      sectionDisplay: mapToSectionDisplayModel(
        resourceData?.sectionInfo as RawSectionInfo,
        {
          paused: resourceData?.paused ?? false,
          progressStatus: resourceData?.pendingAction?.status,
        },
        isFullModuleAttached,
      ),
      ariaLabelConfig: {
        cardComponent: t("search_module_card_title_aria_label", {
          moduleTitle: resourceData?.title,
        }),
        gamification: t("search_module_card_gamification_aria_label"),
        viewDetails: t("search_view_details_aria_label", {
          title: resourceData?.title,
        }),
      },
      tooltipConfig: buildTooltipConfig(resourceData),
    };
  };

  const isAssociatedStudentAccessibleOnlyMoeModule = (resourceData) => {
    return (
      ResourceSearchCardUtils.checkIfMoe(resourceData?.resourceDomain) &&
      resourceData?.accessibleByAssociatedStudent &&
      !resourceData?.accessibleByTeacher
    );
  };
  const populateModuleCardListeners = (resourceData: LessonResultItem) => {
    return {
      click: () => handleViewLessonEditor(resourceData),
      copy: () => handleCopyTo(resourceData),
      viewDetails: () => handleViewLessonDetails(resourceData),
      viewReview: () => handleViewReview(resourceData),
    };
  };

  const handleViewLessonEditor = (resourceData) => {
    // TODO: SLS-11043 (OK), to refactor, use router.resolve
    let routeName = "";
    let routeParams: EditorRouteParams = {};
    let sectionId = null;
    const routeQuery: LocationQueryRaw = {};
    const routeToSelfStudyAttempt =
      isAssociatedStudentAccessibleOnlyMoeModule(resourceData) &&
      isTeacher.value &&
      isAssociatedAccount.value;

    if (resourceData?.sectionInfo && resourceData?.sectionInfo.sectionDisplayModel) {
      sectionId = resourceData?.sectionInfo.sectionDisplayModel.id;
    }

    const resourceUuid = resourceData?.resourceUuid
      ? resourceData?.resourceUuid
      : resourceData?.uuid;
    switch (resourceData?.resourceDomain) {
      case "PERSONAL":
        routeName = isNull(sectionId) ? "EditorCover" : "EditorSectionCover";
        routeParams = {
          resourceDomain: "my-drive",
          mode: "view",
          lessonUuid: resourceUuid,
          sectionId: sectionId,
        };
        break;
      case "MOE":
        routeQuery.version = resourceUuid ? resourceUuid : undefined;
        // SLS-11275 / MOESLSOPS-12498: use module/open gateway for all roles.
        // MoeLessonOpen routes teachers → module/view, students → module/attempt.
        if (routeToSelfStudyAttempt) {
          routeName = "SelfStudyAttemptCover";
          routeParams = {
            lessonUuid: resourceData.moeLibraryUuid,
          };
        } else {
          routeName = "MoeLessonOpen";
          routeParams = {
            lessonUuid: resourceData.moeLibraryUuid,
            ...(sectionId ? { pathMatch: [`section/${sectionId}`] } : {}),
          };
        }
        break;
      case "COMMUNITY":
        routeName = isNull(sectionId) ? "EditorCover" : "EditorSectionCover";
        routeParams = {
          resourceDomain: "community-gallery",
          mode: "view",
          lessonUuid: resourceData?.communityResourceUuid ?? resourceData?.communityResource?.uuid,
          sectionId: sectionId,
        };
        break;
    }
    handleOpenModule({ routeName, routeParams, routeQuery });
  };
  const handleViewLessonDetails = (resourceData) => {
    const subpageParentTitle = [];
    const resourceUuid = resourceData?.resourceUuid
      ? resourceData?.resourceUuid
      : resourceData?.uuid;
    contentSubpageInstance.show(SearchModuleDetailContentSubpage, {
      baseTitle: subpageParentTitle,
      hasParentSubpage: resourceData?.showParentSubpage || !isEmpty(subpageParentTitle),
      uuid: resourceUuid,
      assignmentUuid: resourceData?.lessonAssignmentUuid,
      resourceDomain: resourceData?.resourceDomain,
      title: resourceData?.title,
      type: "LESSON",
      location: getLocationLabel(resourceData?.resourceDomain),
      hasSelectAction: !isStudent.value,
      canViewInNewTab: true,
      showMoreInformation: true,
      sectionId: null,
      addFromEditorLocation: null,
      folderUuid: null,
      targetPageIdx: null,
      redirectAfterCreated: false,
      ownerGroupId: null,
      isAssociatedStudentAccessibleOnlyMoeModule:
        isAssociatedStudentAccessibleOnlyMoeModule(resourceData),
    });
  };
  const handleViewReview = (resourceData) => {
    const resourceUuid = resourceData?.resourceUuid
      ? resourceData?.resourceUuid
      : resourceData?.uuid;
    contentSubpageInstance.show(
      LessonReviewContentSubpage,
      {
        baseTitle: "",
        hasParentSubpage: resourceData?.showParentSubpage,
        uuid: resourceUuid,
        title: resourceData?.title,
        resourceDomain: resourceData?.resourceDomain,
        resourceStatus: resourceData?.status,
      },
      {
        teacherReviewCountUpdated: (count: number) => {
          resourceData.teacherReviewCount = count;
        },
      },
    );
  };
  const handleCopyTo = (resourceData) => {
    contentSubpageInstance.show(
      ResourceCopyContentSubpage,
      {
        resourceUuid: "",
        type: RESOURCE_TYPE.FOLDER,
        actionType: COPY_ACTION_TYPE.COPY_LESSON,
        resources: [resourceData],
        waitActionPrimary: "route",
      },
      {
        onResourceSelected: (data) => handleCopyModuleRedirect(data),
      },
    );
  };

  const handleCopyModuleRedirect = (data: { type: string; uuid: string }) => {
    router.push({
      name: "MyDriveHome",
      params: {
        folderUuid: data.type === RESOURCE_NODE_TYPE.FOLDER ? data.uuid : "",
      },
    });
  };

  const handleOpenModule = ({ routeName, routeParams, routeQuery }) => {
    handleRouteIntervalCheck();
    target.value = "";
    const routeData = router.resolve({
      name: routeName,
      params: routeParams,
      query: routeQuery,
    });
    urlReady.value = routeData.href;
  };

  return {
    populateModuleCardProps,
    populateModuleCardPropsAddFromLibrary,
    populateModuleCardPropsStarredResource,
    populateModuleCardPropsSelfStudy,
    populateModuleCardPropsPlannerAttachment,
    populateModuleCardPropsClassGroupResource,
    populateModuleCardPropsCalendarAttachment,
    populateModuleCardListeners,
  };
};
